(() => {
  function initProductDescription(wrapper) {
    if (!wrapper || wrapper.dataset.productDescriptionInitialized === 'true') return;

    const content = wrapper.querySelector('[data-product-description-content]');
    const toggle = wrapper.querySelector('[data-product-description-toggle]');

    if (!content || !toggle) return;

    wrapper.dataset.productDescriptionInitialized = 'true';

    const collapsedHeight = 275;

    if (content.scrollHeight <= collapsedHeight) return;

    wrapper.classList.add('is-collapsed');
    content.classList.add('is-collapsed');
    toggle.hidden = false;

    toggle.addEventListener('click', () => {
      const isExpanded = content.classList.toggle('is-expanded');
      const expandText = toggle.dataset.expandText || 'See more';
      const collapseText = toggle.dataset.collapseText || 'See less';

      content.classList.toggle('is-collapsed', !isExpanded);
      wrapper.classList.toggle('is-collapsed', !isExpanded);

      toggle.textContent = isExpanded ? collapseText : expandText;
      toggle.setAttribute('aria-expanded', String(isExpanded));

      if (!isExpanded) {
        wrapper.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    });
  }

  function parseVariants(form) {
    if (!form) return [];

    const variantsScript = form.querySelector('[data-product-variants-json]');

    if (!variantsScript) return [];

    try {
      return JSON.parse(variantsScript.textContent);
    } catch (error) {
      console.warn('Could not parse product variants JSON.', error);
      return [];
    }
  }

  function getSelectedOptions(form) {
    const optionControls = Array.from(
      form.querySelectorAll('input[name^="options["]:checked, select[name^="options["]')
    );

    return optionControls.map((control) => control.value);
  }

  function getSelectedVariant(form, variants) {
    const selectedOptions = getSelectedOptions(form);

    if (!selectedOptions.length) return null;

    return variants.find((variant) => {
      if (!Array.isArray(variant.options)) return false;

      return variant.options.every((option, index) => {
        return String(option) === String(selectedOptions[index]);
      });
    });
  }

  function selectVariantImage(form, variant) {
    if (!variant || !variant.featured_image_id) return;

    const productShowcase = form.closest('.product-showcase');
    const gallery =
      productShowcase?.querySelector('[data-product-gallery]') ||
      document.querySelector('[data-product-gallery]');

    if (!gallery) return;

    gallery.dispatchEvent(
      new CustomEvent('product-gallery:select-image', {
        detail: {
          imageId: String(variant.featured_image_id)
        }
      })
    );
  }

  function initVariantGallerySync(form) {
    if (!form || form.dataset.variantGallerySyncInitialized === 'true') return;

    const variants = parseVariants(form);

    if (!variants.length) return;

    const optionControls = form.querySelectorAll(
      'input[name^="options["], select[name^="options["]'
    );

    if (!optionControls.length) return;

    form.dataset.variantGallerySyncInitialized = 'true';

    optionControls.forEach((control) => {
      control.addEventListener('change', () => {
        const selectedVariant = getSelectedVariant(form, variants);
        selectVariantImage(form, selectedVariant);
      });
    });
  }

  function copyTextToClipboard(text) {
    if (navigator.clipboard && window.isSecureContext) {
      return navigator.clipboard.writeText(text);
    }

    return new Promise((resolve, reject) => {
      const textarea = document.createElement('textarea');

      textarea.value = text;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.top = '-9999px';
      textarea.style.left = '-9999px';

      document.body.appendChild(textarea);
      textarea.select();

      try {
        const successful = document.execCommand('copy');
        document.body.removeChild(textarea);

        if (successful) {
          resolve();
        } else {
          reject(new Error('Copy command was not successful.'));
        }
      } catch (error) {
        document.body.removeChild(textarea);
        reject(error);
      }
    });
  }

  function getCurrentShareUrl(shareWrapper) {
    const baseUrl = shareWrapper.dataset.productUrl || window.location.href.split('?')[0];
    const includeVariant = shareWrapper.dataset.shareIncludeVariant === 'true';

    if (!includeVariant) return baseUrl;

    const productShowcase = shareWrapper.closest('.product-showcase');
    const form = productShowcase?.querySelector('form');

    if (!form) return baseUrl;

    const variants = parseVariants(form);
    const selectedVariant = variants.length ? getSelectedVariant(form, variants) : null;
    const selectedVariantId =
      selectedVariant?.id ||
      form.querySelector('input[name="id"], select[name="id"]')?.value;

    if (!selectedVariantId) return baseUrl;

    try {
      const url = new URL(baseUrl, window.location.origin);
      url.searchParams.set('variant', selectedVariantId);
      return url.toString();
    } catch (error) {
      console.warn('Could not build variant share URL.', error);
      return baseUrl;
    }
  }

  function showShareStatus(status, message) {
    if (!status) return;

    status.textContent = message;

    window.clearTimeout(status.productShareTimer);

    status.productShareTimer = window.setTimeout(() => {
      status.textContent = '';
    }, 2500);
  }

  function initProductShare(shareWrapper) {
    if (!shareWrapper || shareWrapper.dataset.productShareInitialized === 'true') return;

    const button = shareWrapper.querySelector('[data-product-share-button]');
    const status = shareWrapper.querySelector('[data-product-share-status]');

    if (!button) return;

    shareWrapper.dataset.productShareInitialized = 'true';

    button.addEventListener('click', async () => {
      const url = getCurrentShareUrl(shareWrapper);
      const title = shareWrapper.dataset.productTitle || document.title;
      const text = shareWrapper.dataset.productText || title;
      const copiedText = shareWrapper.dataset.copiedText || 'Link copied!';

      const shareData = {
        title,
        text,
        url
      };

      try {
        if (navigator.share && (!navigator.canShare || navigator.canShare(shareData))) {
          await navigator.share(shareData);
          return;
        }
      } catch (error) {
        if (error.name === 'AbortError') return;

        console.warn('Native product sharing failed. Falling back to copy link.', error);
      }

      try {
        await copyTextToClipboard(url);
        showShareStatus(status, copiedText);
      } catch (error) {
        console.warn('Could not copy product link.', error);
        showShareStatus(status, 'Could not copy link');
      }
    });
  }

  function initProductDescriptions(container = document) {
    container
      .querySelectorAll('[data-product-description-wrap]')
      .forEach(initProductDescription);
  }

  function initVariantGallerySyncs(container = document) {
    container
      .querySelectorAll('form [data-product-variants-json]')
      .forEach((variantsScript) => {
        const form = variantsScript.closest('form');
        initVariantGallerySync(form);
      });
  }

  function initProductShares(container = document) {
    container
      .querySelectorAll('[data-product-share]')
      .forEach(initProductShare);
  }

  function initProductInfo(container = document) {
    initProductDescriptions(container);
    initVariantGallerySyncs(container);
    initProductShares(container);
  }

  document.addEventListener('DOMContentLoaded', () => {
    initProductInfo();
  });

  document.addEventListener('shopify:section:load', (event) => {
    initProductInfo(event.target);
  });
})();
